// src/lib/gamification.js
// Pure, Firebase-free gamification rules. Easy to tune and unit-test.

// ── XP ────────────────────────────────────────────────────────────────────────
// 10 XP per correct answer, plus a bonus for strong performance.
export function xpForAttempt({ correct = 0, pct = 0 } = {}) {
  const base = correct * 10;
  const bonus = pct === 100 ? 50 : pct >= 80 ? 20 : 0;
  return base + bonus;
}

// ── Levels ────────────────────────────────────────────────────────────────────
// Cumulative XP needed to *reach* a level: level n requires 100 * n*(n-1)/2.
// Level 1 = 0 XP, Level 2 = 100, Level 3 = 300, Level 4 = 600, Level 5 = 1000 ...
export function xpForLevel(level) {
  const n = Math.max(1, level);
  return 100 * (n * (n - 1)) / 2;
}

export function levelForXp(xp = 0) {
  let level = 1;
  while (xp >= xpForLevel(level + 1)) level++;
  return level;
}

// Progress info toward the next level — handy for a progress bar.
export function levelProgress(xp = 0) {
  const level = levelForXp(xp);
  const floor = xpForLevel(level);
  const ceil = xpForLevel(level + 1);
  const span = ceil - floor || 1;
  return {
    level,
    nextLevel: level + 1,
    intoLevel: xp - floor,
    levelSpan: span,
    toNext: Math.max(0, ceil - xp),
    pct: Math.min(100, Math.round(((xp - floor) / span) * 100)),
  };
}

// ── Streaks ─────────────────────────────────────────────────────────────────
const DAY_MS = 24 * 60 * 60 * 1000;

// ISO date string (YYYY-MM-DD) for a Date in local time.
export function isoDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 10);
}

// Given the previous streak object and today's ISO day, return the next streak.
export function nextStreak(prev = {}, todayISO = isoDay()) {
  const current = prev.current || 0;
  const longest = prev.longest || 0;
  const last = prev.lastActiveDate || null;

  if (last === todayISO) {
    // Already counted today — unchanged.
    return { current: current || 1, longest: Math.max(longest, current || 1), lastActiveDate: todayISO };
  }

  const yesterday = isoDay(new Date(new Date(todayISO + "T00:00:00").getTime() - DAY_MS));
  const newCurrent = last === yesterday ? current + 1 : 1;
  return {
    current: newCurrent,
    longest: Math.max(longest, newCurrent),
    lastActiveDate: todayISO,
  };
}

// ── Badges ────────────────────────────────────────────────────────────────────
// Each badge: { id, name, description, icon, test(ctx) -> boolean }
// ctx = { profile (the UPDATED profile snapshot), attempt, topicStat }
// `tier` groups related badges (bronze → silver → gold) and powers tiered
// styling in the badge showcase. Existing ids are kept stable so already-earned
// badges and profile hints keep working.
export const BADGES = [
  // ── Getting started / quizzes ──────────────────────────────────────────────
  {
    id: "first-quiz",
    name: "First Steps",
    description: "Complete your first quiz",
    icon: "🎯",
    tier: "bronze",
    test: ({ profile }) => (profile.totalAttempts || 0) >= 1,
  },
  {
    id: "ten-quizzes",
    name: "Dedicated",
    description: "Complete 10 quizzes",
    icon: "📚",
    tier: "silver",
    test: ({ profile }) => (profile.totalAttempts || 0) >= 10,
  },
  {
    id: "fifty-quizzes",
    name: "Scholar",
    description: "Complete 50 quizzes",
    icon: "🎓",
    tier: "gold",
    test: ({ profile }) => (profile.totalAttempts || 0) >= 50,
  },
  {
    id: "hundred-quizzes",
    name: "Centurion",
    description: "Complete 100 quizzes",
    icon: "🏅",
    tier: "gold",
    test: ({ profile }) => (profile.totalAttempts || 0) >= 100,
  },

  // ── Accuracy ───────────────────────────────────────────────────────────────
  {
    id: "perfect-score",
    name: "Flawless",
    description: "Score 100% on a quiz",
    icon: "💯",
    tier: "gold",
    test: ({ attempt }) => attempt && attempt.pct === 100,
  },
  {
    id: "topic-master",
    name: "Topic Master",
    description: "Reach 90%+ accuracy on a topic",
    icon: "🏆",
    tier: "gold",
    test: ({ topicStat }) => topicStat && topicStat.attempts >= 2 && topicStat.accuracy >= 0.9,
  },

  // ── Streaks ────────────────────────────────────────────────────────────────
  {
    id: "streak-3",
    name: "Warming Up",
    description: "Practise 3 days in a row",
    icon: "✨",
    tier: "bronze",
    test: ({ profile }) => (profile.streak?.current || 0) >= 3,
  },
  {
    id: "streak-7",
    name: "On Fire",
    description: "Practise 7 days in a row",
    icon: "🔥",
    tier: "silver",
    test: ({ profile }) => (profile.streak?.current || 0) >= 7,
  },
  {
    id: "streak-30",
    name: "Unstoppable",
    description: "Practise 30 days in a row",
    icon: "⚡",
    tier: "gold",
    test: ({ profile }) => (profile.streak?.current || 0) >= 30,
  },

  // ── Levels ─────────────────────────────────────────────────────────────────
  {
    id: "level-5",
    name: "Rising Star",
    description: "Reach level 5",
    icon: "⭐",
    tier: "silver",
    test: ({ profile }) => (profile.level || 1) >= 5,
  },
  {
    id: "level-10",
    name: "Expert",
    description: "Reach level 10",
    icon: "🌟",
    tier: "gold",
    test: ({ profile }) => (profile.level || 1) >= 10,
  },
  {
    id: "level-25",
    name: "Legend",
    description: "Reach level 25",
    icon: "👑",
    tier: "gold",
    test: ({ profile }) => (profile.level || 1) >= 25,
  },
];

// Tier → accent classes for the badge showcase (earned state).
export const TIER_STYLES = {
  bronze: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800", chip: "bg-orange-200 text-orange-800" },
  silver: { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700", chip: "bg-slate-200 text-slate-700" },
  gold:   { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", chip: "bg-amber-200 text-amber-800" },
};

// Returns the ids of newly-earned badges (not already in profile.badges).
export function evaluateBadges(ctx) {
  const owned = new Set(ctx.profile?.badges || []);
  return BADGES.filter((b) => !owned.has(b.id) && (() => { try { return b.test(ctx); } catch { return false; } })())
    .map((b) => b.id);
}

export const badgeById = (id) => BADGES.find((b) => b.id === id);

// Slugify a topic into a stable Firestore-safe document id.
export function topicKey(topic = "") {
  return (
    topic.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 120) || "untitled"
  );
}

// Weakness threshold — accuracy below this flags a topic as "weak".
export const WEAK_THRESHOLD = 0.6;
